import { useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { styled } from '@mui/material/styles'
import Popper from '@mui/material/Popper'
import { intl } from '../../i18n'
import { colorPalette } from '../../view/style/colorPalette'
import {
  buildDemoInquiryUrl,
  DEMO_INQUIRY_LINK_LABEL,
} from './layout/demoInquiry'

// 進捗報告画面のうち、レポートの構成を変更する機能をデモでは使用不可にする。
// 対象は「報告書を作成」（レポート選択メニュー）と「ページ構成編集」
// （ヘッダー）の2つ。どちらもテンプレート・ページ定義の更新APIを伴い、
// MSWの固定データと整合しなくなるため、見た目は残して操作だけ止める。
//
// 本体（view/pages/ProjectVersionReport）は改修せず、この画面がマウント
// されている間だけdocumentのcaptureフェーズでクリック・ホバーを横取りする。
// useDisableExternalNavigationがwindow.openを差し替えているのと同じ考え方で、
// デモ固有の都合を本体へ持ち込まないため。無効化したい対象が本体の奥
// （version-select-menu配下）にあり、propsで制御する口が無いことからも、
// 呼び出し側の改修ではなくこの方式を採っている。
//
// どちらの文言も本体側がハードコードしているため、ロケールに関係なく
// この文字列で一致する（i18n経由にすると英語ロケールで逆に外れる）。
// aria-labelの出どころは2つで異なる:
// - ページ構成編集: PageConfigButton.tsxが自前でaria-labelを持つ
//   （同ボタンのTooltip titleはi18nだが、MUIは children.props を後勝ちで
//   マージするため自前の属性が残る）
// - 報告書を作成: version-select-menu/ReportMenu.tsxのTooltip titleが
//   そのままaria-labelになる（describeChild未指定時のMUIの既定動作）
const DISABLED_BUTTON_LABELS = ['報告書を作成', 'ページ構成編集']

const DISABLED_BUTTON_SELECTOR = DISABLED_BUTTON_LABELS.map(
  label => `[aria-label="${label}"]`
).join(',')

// 「AIで全行の定性コメントを生成」（ヘッダーの…メニュー）はaria-labelを
// 持たないPopper内のListItemButtonなので、表示文言で特定する。
// 文言はi18n経由で引く（日本語をハードコードすると英語ロケールで外れる）。
// この項目は子が文言のみなのでtextContentの完全一致が成立する。アイコンや
// 条件付きテキストを含む項目（コピーURL等）を足す場合は一致しない。
const DISABLED_MENU_ITEM_SELECTOR = '.MuiListItemButton-root'

const DISABLED_MENU_ITEM_MESSAGE_IDS = ['versionReport.evaluate.allRows']

const findDisabledButton = (target: EventTarget | null): Element | null => {
  if (!(target instanceof Element)) return null
  // ボタン内のアイコンがイベント対象になることもあるのでclosestで辿る。
  const byLabel = target.closest(DISABLED_BUTTON_SELECTOR)
  if (byLabel) return byLabel
  const menuItem = target.closest(DISABLED_MENU_ITEM_SELECTOR)
  if (!menuItem) return null
  const text = menuItem.textContent?.trim() ?? ''
  return DISABLED_MENU_ITEM_MESSAGE_IDS.some(
    id => intl.formatMessage({ id }) === text
  )
    ? menuItem
    : null
}

export const DemoVersionReportDisabledFeatures = () => {
  const { pathname } = useLocation()
  const [anchorEl, setAnchorEl] = useState<Element | null>(null)

  useEffect(() => {
    const blockClick = (event: MouseEvent) => {
      if (!findDisabledButton(event.target)) return
      // captureフェーズで止め、本体のonClickまで到達させない。
      event.preventDefault()
      event.stopPropagation()
    }

    // 本体のTooltip（「報告書を作成」等の通常の説明）も同時に出るが、
    // こちらの案内の方が前面（DEMO_NOTICE_Z_INDEX）に表示される。
    const showNotice = (event: MouseEvent) => {
      const button = findDisabledButton(event.target)
      if (button) setAnchorEl(button)
    }

    const hideNotice = (event: MouseEvent) => {
      const button = findDisabledButton(event.target)
      if (!button) return
      // 案内内のリンクへポインタを移す間に閉じないよう猶予を持たせる。
      const related = event.relatedTarget
      if (related instanceof Element && related.closest(`.${NOTICE_CLASS}`)) {
        return
      }
      setAnchorEl(null)
    }

    document.addEventListener('click', blockClick, true)
    document.addEventListener('mouseover', showNotice, true)
    document.addEventListener('mouseout', hideNotice, true)
    return () => {
      document.removeEventListener('click', blockClick, true)
      document.removeEventListener('mouseover', showNotice, true)
      document.removeEventListener('mouseout', hideNotice, true)
    }
  }, [])

  return (
    <Popper
      open={Boolean(anchorEl)}
      anchorEl={anchorEl}
      placement="bottom-start"
      style={{ zIndex: DEMO_NOTICE_Z_INDEX }}
    >
      <NoticeBody
        className={NOTICE_CLASS}
        onMouseLeave={() => setAnchorEl(null)}
      >
        <span>
          {intl.formatMessage({ id: 'demo.warning.featureDisabled' })}
        </span>
        <InquiryLink
          href={buildDemoInquiryUrl(pathname)}
          target="_blank"
          rel="noopener noreferrer"
        >
          {DEMO_INQUIRY_LINK_LABEL}
        </InquiryLink>
      </NoticeBody>
    </Popper>
  )
}

// ポインタが案内本体へ移った際に閉じないようにするための目印。
const NOTICE_CLASS = 'demo-disabled-feature-notice'

// MUIの既定 zIndex.tooltip（1500）より上。無効化の案内が本体のTooltipや
// ダイアログ（DIALOG_Z_INDEX=1200）に隠れてはいけないため。
const DEMO_NOTICE_Z_INDEX = 1600

// 見た目はDemoDisabledAction（ヘッダー等の無効化UI）のTooltipに合わせる。
const NoticeBody = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: '6px',
  margin: '6px 0',
  padding: '8px 12px',
  maxWidth: '360px',
  borderRadius: '4px',
  border: `1px solid ${colorPalette.monotone[2]}`,
  backgroundColor: colorPalette.monotone[1],
  color: colorPalette.monotone[4],
  boxShadow: '0px 2px 6px 0px #7B8CAA66',
  fontSize: '14px',
  whiteSpace: 'nowrap',
})

const InquiryLink = styled('a')({
  color: colorPalette.skyBlue[8],
  textDecoration: 'underline',
  textUnderlineOffset: '2px',
  fontSize: '13px',
  cursor: 'pointer',
  '&:hover': {
    opacity: 0.8,
  },
})
